import type { ReactNode } from "react";
import { Check, Info, Loader2 } from "lucide-react";

/**
 * Piezas compartidas por los pasos del asistente guiado. Nada de lógica aquí:
 * solo lo que se repite en los seis pasos, para que todos se vean igual.
 */

export const INPUT_CLASS =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm";

export const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1";

/** Etiqueta de un paso: si es imprescindible para recibir reservas o no. */
export function Etiqueta({ obligatorio }: { obligatorio: boolean }) {
  return obligatorio ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
      Imprescindible
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
      Opcional
    </span>
  );
}

/** Cuadro de contexto (por qué hace falta esto, qué pasa si no está). */
export function Aviso({
  children,
  tono = "info",
}: {
  children: ReactNode;
  tono?: "info" | "exito" | "ojo";
}) {
  const estilos = {
    info: "bg-sky-50 border-sky-200 text-sky-900",
    exito: "bg-emerald-50 border-emerald-200 text-emerald-900",
    ojo: "bg-amber-50 border-amber-200 text-amber-900",
  }[tono];
  return (
    <div className={`flex gap-2 rounded-lg border p-3 text-sm ${estilos}`}>
      {tono === "exito" ? (
        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
      ) : (
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
      )}
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

/** Línea de algo que YA está creado, con su check. */
export function FilaHecha({
  titulo,
  detalle,
  accion,
}: {
  titulo: string;
  detalle?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
        <Check className="w-3 h-3" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{titulo}</p>
        {detalle && <p className="text-xs text-gray-500 truncate">{detalle}</p>}
      </div>
      {accion}
    </div>
  );
}

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" />
      {texto}
    </div>
  );
}

/** Hora en formato HH:MM. Input nativo: en móvil abre el reloj del sistema. */
export function Hora({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  return (
    <input
      type="time"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
    />
  );
}

/** Botón discreto (añadir turno, quitar, copiar horario…). */
export function BotonSuave({
  onClick,
  children,
  tono = "normal",
  disabled,
}: {
  onClick: () => void;
  children: ReactNode;
  tono?: "normal" | "peligro";
  disabled?: boolean;
}) {
  const estilo =
    tono === "peligro"
      ? "text-red-600 hover:bg-red-50"
      : "text-primary-700 hover:bg-primary-50";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${estilo}`}
    >
      {children}
    </button>
  );
}
